import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import parsePhoneNumberFromString, {
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import PageAlert from "../../components/PageAlert/PageAlert";
import {fetchWebsiteSettings,saveWebsiteSettings} from "../../api/websiteSettingApi";
import { resolveApiError } from "../../utils/apiError";
import "./settings.scss";

const DEFAULT_COUNTRY = "MY";
const DEFAULT_DIAL_CODE = getCountryCallingCode(DEFAULT_COUNTRY);
const regionNames = typeof Intl !== "undefined" && Intl.DisplayNames
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;

const dialCodes = getCountries()
  .map((iso) => ({
    iso,
    dial: getCountryCallingCode(iso),
    name: regionNames?.of(iso) || iso,
    flag: [...iso].map((char) => String.fromCodePoint(127397 + char.charCodeAt(0))).join(""),
  }))
  .sort((left, right) => left.name.localeCompare(right.name));

const splitPhoneNumber = (number = "") => {
  const digits = String(number).replace(/\D/g, "");
  const parsed = parsePhoneNumberFromString(digits ? `+${digits}` : "");
  if (parsed?.countryCallingCode) {
    return {
      countryIso: parsed.country || DEFAULT_COUNTRY,
      localNumber: String(parsed.nationalNumber || ""),
    };
  }
  return { countryIso: DEFAULT_COUNTRY, localNumber: digits };
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const [websiteOnline, setWebsiteOnline] = useState(true);
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY);
  const [whatsapp, setWhatsapp] = useState("");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [dialOpen, setDialOpen] = useState(false);
  const dialRef = useRef(null);
  const selectedCountry = dialCodes.find((country) => country.iso === countryIso) || dialCodes.find((country) => country.iso === DEFAULT_COUNTRY);

  useEffect(() => {
    if (!dialOpen) return undefined;
    const onPointerDown = (event) => {
      if (dialRef.current && !dialRef.current.contains(event.target)) setDialOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setDialOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [dialOpen]);

  useEffect(()=>{fetchWebsiteSettings().then(data=>{const phone=splitPhoneNumber(data.whatsapp_number);setWebsiteOnline(data.website_online);setCountryIso(phone.countryIso);setWhatsapp(phone.localNumber);}).catch(e=>resolveApiError(e,setError,navigate)).finally(()=>setLoading(false));},[navigate]);

  const saveSettings = async (event) => {
    event.preventDefault();
    let dialCode = DEFAULT_DIAL_CODE;
    try { dialCode = getCountryCallingCode(countryIso); } catch { /* keep default */ }
    const localNumber = whatsapp.replace(/\D/g, "").replace(/^0+/, "");
    const normalized = `${dialCode}${localNumber}`;
    if (!/^[1-9][0-9]{7,14}$/.test(normalized)) {
      setError("Enter a valid WhatsApp phone number.");
      setSaved(false);
      return;
    }
    setError("");setSaving(true);
    try{const data=await saveWebsiteSettings({website_online:websiteOnline,whatsapp_number:normalized});const phone=splitPhoneNumber(data.whatsapp_number);setWebsiteOnline(data.website_online);setCountryIso(phone.countryIso);setWhatsapp(phone.localNumber);setSaved(true);}catch(e){resolveApiError(e,setError,navigate);setSaved(false);}finally{setSaving(false);}
  };

  return (
    <div className="settings-page">
      {saved && <PageAlert variant="success" onDismiss={() => setSaved(false)}>Website settings saved.</PageAlert>}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
      <section className="settings-page__heading">
        <h2>Website settings</h2>
        <p>Configure storefront availability and the WhatsApp enquiry contact.</p>
      </section>

      <form className="settings-page__form" onSubmit={saveSettings}>
        <section className="settings-page__card">
          <div className="settings-page__card-head">
            <div><p>WEBSITE STATUS</p><h3>Storefront availability</h3></div>
            <span className={`settings-page__badge ${websiteOnline ? "settings-page__badge--online" : "settings-page__badge--offline"}`}>{websiteOnline ? "Online" : "Offline"}</span>
          </div>
          <div className="settings-page__setting">
            <div><strong>Website is available to visitors</strong><small>When offline, visitors will see a temporary maintenance page.</small></div>
            <button type="button" className={`settings-page__switch ${websiteOnline ? "settings-page__switch--on" : ""}`} onClick={() => { setWebsiteOnline((value) => !value); setSaved(false); }} aria-label="Toggle website status"><i /></button>
          </div>
          {!websiteOnline && <div className="settings-page__warning">The public website will be unavailable after these settings are saved. The admin portal will remain accessible.</div>}
        </section>

        <section className="settings-page__card">
          <div className="settings-page__card-head"><div><p>WHATSAPP</p><h3>Enquiry contact</h3></div></div>
          <div className="settings-page__field">
            <span>WhatsApp number</span>
            <div className="settings-page__phone">
              <b>WA</b>
              <div className="settings-page__dial" ref={dialRef}>
                <button
                  type="button"
                  className="settings-page__dial-toggle"
                  aria-label="Country dial code"
                  aria-expanded={dialOpen}
                  onClick={() => setDialOpen((open) => !open)}
                >
                  {selectedCountry?.flag} +{selectedCountry?.dial}
                </button>
                {dialOpen && (
                  <ul className="settings-page__dial-menu" role="listbox">
                    {dialCodes.map((country) => (
                      <li key={country.iso}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={country.iso === countryIso}
                          className={country.iso === countryIso ? "is-selected" : ""}
                          onClick={() => {
                            setCountryIso(country.iso);
                            setDialOpen(false);
                            setError("");
                            setSaved(false);
                          }}
                        >
                          {country.flag} +{country.dial} — {country.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <input inputMode="tel" value={whatsapp} onChange={(event) => { setWhatsapp(event.target.value); setError(""); setSaved(false); }} placeholder="12 345 6789" />
            </div>
            <small>Select the country dial code and enter the phone number without its leading zero. This number is used by all WhatsApp enquiry buttons on the website.<br />Example: 012-345 6789 → +60 123456789</small>
          </div>
        </section>

        <div className="settings-page__actions"><button className="ui-button ui-button--primary" type="submit" disabled={loading||saving}>{saving?"Saving…":"Save changes"}</button></div>
      </form>
    </div>
  );
};

export default SettingsPage;
